import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users as UsersIcon, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BrandChip } from "@/components/BrandChip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contributors() {
  const [contribs, setContribs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/contributors`)
      .then((r) => setContribs(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/5"
                asChild
              >
                <Link to="/" data-testid="contrib-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
              <h1 className="font-serif font-bold text-lg text-white">Contributors</h1>
            </div>
            <BrandChip size={40} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.32em] text-amber-500 font-semibold mb-2">
            With gratitude
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-3">
            The people behind this archive
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            Everyone below has contributed at least one approved depot, tower, or
            correction. Their names are shown here because they opted in to public
            credit. Emails are never displayed.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-white/50">Loading…</div>
        ) : contribs.length === 0 ? (
          <Card className="bg-[#111] border-white/5 max-w-2xl mx-auto">
            <CardContent className="p-10 text-center">
              <UsersIcon className="w-14 h-14 text-white/20 mx-auto mb-4" />
              <p className="text-white/70 mb-2 text-lg">No public contributors yet.</p>
              <p className="text-white/50 text-sm mb-6">
                Be the first — <Link to="/submit" className="text-amber-500 hover:underline">contribute a location</Link>{" "}
                and tick the "credit me publicly" box.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="contributors-grid"
          >
            {contribs.map((c, i) => (
              <Card
                key={`${c.name}-${i}`}
                className="bg-[#111] border-white/5 hover:border-amber-500/30 transition-colors"
                data-testid={`contributor-card-${i}`}
              >
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-serif text-lg shrink-0">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{c.name}</p>
                    <p className="text-xs text-white/50 mt-0.5">
                      {c.count} {c.count === 1 ? "contribution" : "contributions"}
                      {c.first_seen && (
                        <>
                          <span className="mx-1.5 text-white/25">·</span>
                          since {new Date(c.first_seen).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                        </>
                      )}
                    </p>
                  </div>
                  {c.count >= 5 && (
                    <Heart className="w-4 h-4 text-red-400/70 shrink-0 mt-1" aria-label="frequent contributor" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="mt-12 text-center text-xs text-white/40 max-w-xl mx-auto">
          Want to remove yourself from this list, or change how your name appears?
          Email <span className="text-white/70">takedown@railstream.net</span> and we&apos;ll update
          the record within 24 hours.
        </p>
      </main>
    </div>
  );
}
